import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './RefundQueueManagement.css';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';

export default function RefundQueueManagement() {
  const { apiFetch, hasPermission } = useAuth();
  const { t } = useI18n();

  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [theme, setTheme] = useState(() => {
    try {
      return document.documentElement.getAttribute('data-theme') || 'dark';
    } catch (e) {
      return 'dark';
    }
  });
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ status: statusFilter });
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      qs.set('limit', String(1000));
      const res = await apiFetch(`/api/refund/queue?${qs.toString()}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to load queue');
      }
      setItems(Array.isArray(data) ? data : []);
      setSelectedIds([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, statusFilter, debouncedSearch]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // observe the global document theme and apply it to this page wrapper
  useEffect(() => {
    const applyGlobal = () => {
      const global = document.documentElement.getAttribute('data-theme') || 'dark';
      setTheme(global);
      const el = document.querySelector('.refund-queue-page');
      if (el) el.setAttribute('data-theme', global);
    };
    applyGlobal();
    const obs = new MutationObserver(() => applyGlobal());
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allVisibleSelected = useMemo(() => items.length > 0 && items.every((it) => selectedIds.includes(it.id)), [items, selectedIds]);

  const stats = useMemo(() => {
    const pending = items.filter((it) => it.status === 'pending').length;
    const failed = items.filter((it) => it.status === 'failed').length;
    const done = items.filter((it) => it.status === 'done').length;
    return {
      total: items.length,
      pending,
      failed,
      done
    };
  }, [items]);

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !items.some((it) => it.id === id)));
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      items.forEach((it) => next.add(it.id));
      return Array.from(next);
    });
  };

  const retrySelected = async () => {
    if (!selectedIds.length) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiFetch('/api/refund/queue/retry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Retry failed');
      }
      const okCount = (data.results || []).filter((r) => r.success).length;
      const failCount = (data.results || []).length - okCount;
      setSuccess(t('refundQueue.retrySummary', { ok: okCount, failed: failCount }));
      await loadQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    const confirmed = window.confirm(t('refundQueue.deleteConfirm', { count: selectedIds.length }));
    if (!confirmed) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiFetch('/api/refund/queue/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Delete failed');
      }
      setSuccess(t('refundQueue.deleteSummary', { count: data.deleted || 0 }));
      await loadQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const retryRow = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/refund/queue/retry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Retry failed');
      setSuccess(t('refundQueue.retrySummary', { ok: (data.results || []).filter(r=>r.success).length, failed: (data.results || []).length - (data.results || []).filter(r=>r.success).length }));
      await loadQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm(t('refundQueue.deleteConfirm', { count: 1 }))) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/refund/queue/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setSuccess(t('refundQueue.deleteSummary', { count: data.deleted || 1 }));
      await loadQueue();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!hasPermission('refund.view')) {
    return (
      <div className="refund-queue-container">
        <div className="refund-queue-error">{t('refund.noPermission')}</div>
      </div>
    );
  }

  return (
    <div className="refund-queue-page" data-theme={theme}>
      <div className="refund-queue-container">
        <div className="refund-queue-header">
        <div className="refund-queue-header-content">
          <div className="refund-queue-header-icon">RQ</div>
          <div>
            <h2>{t('refundQueue.title')}</h2>
            <p>{t('refundQueue.subtitle')}</p>
          </div>
        </div>
        <div className="refund-queue-header-meta">
          <div className="refund-queue-meta-line">{t('refundQueue.totalItems', { count: stats.total })}</div>
          <div className="refund-queue-meta-actions">
            <button className="btn btn-secondary" onClick={loadQueue}>{t('refundQueue.refresh')}</button>
          </div>
        </div>
      </div>

      {error && <div className="refund-queue-error">{error}</div>}
      {success && <div className="refund-queue-success">{success}</div>}

        <div className="refund-queue-stats">
        <div className="refund-queue-stat-box">
          <div className="refund-queue-stat-label">{t('refundQueue.pending')}</div>
          <div className="refund-queue-stat-value pending">{stats.pending}</div>
        </div>
        <div className="refund-queue-stat-box">
          <div className="refund-queue-stat-label">{t('refundQueue.failed')}</div>
          <div className="refund-queue-stat-value failed">{stats.failed}</div>
        </div>
        <div className="refund-queue-stat-box">
          <div className="refund-queue-stat-label">{t('refundQueue.done')}</div>
          <div className="refund-queue-stat-value done">{stats.done}</div>
        </div>
      </div>

        <div className="refund-queue-section">
        <div className="refund-queue-toolbar">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="refund-queue-select">
            <option value="pending">{t('refundQueue.pending')}</option>
            <option value="failed">{t('refundQueue.failed')}</option>
            <option value="done">{t('refundQueue.done')}</option>
            <option value="all">{t('refundQueue.all')}</option>
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="refund-queue-search"
            placeholder={t('refundQueue.searchPlaceholder')}
          />
        
        </div>

        <div className="refund-queue-actions">
          <button className="btn btn-secondary" onClick={toggleSelectAllVisible} disabled={loading || items.length === 0}>
            {allVisibleSelected ? t('refundQueue.unselectAll') : t('refundQueue.selectAll')}
          </button>
          <button className="btn btn-primary" onClick={retrySelected} disabled={loading || selectedIds.length === 0 || !hasPermission('refund.edit')}>
            {t('refundQueue.retrySelected')}
          </button>
          <button className="btn btn-danger" onClick={deleteSelected} disabled={loading || selectedIds.length === 0 || !hasPermission('refund.edit')}>
            {t('refundQueue.deleteSelected')}
          </button>
        </div>
      </div>

        <div className="refund-queue-section">
        <div className="refund-queue-table-wrap">
          <table className="refund-queue-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>{t('refundQueue.player')}</th>
                <th>{t('refundQueue.item')}</th>
                <th>{t('refundQueue.quantity')}</th>
                <th>{t('refundQueue.status')}</th>
                <th>{t('refundQueue.attempts')}</th>
                <th>{t('refundQueue.delivery')}</th>
                <th>{t('refundQueue.lastError')}</th>
                <th>{t('refundQueue.created')}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="10" className="refund-queue-empty">{loading ? t('refundQueue.loading') : t('refundQueue.empty')}</td>
                </tr>
              ) : (
                items.slice((page-1)*pageSize, page*pageSize).map((row) => (
                  <tr key={row.id}>
                    <td>
                      <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)} />
                    </td>
                    <td>{row.id}</td>
                    <td>
                      <div className="player-identifier-cell">
                        <div className="player-id">{row.player_identifier}</div>
                        {row.player_name ? <div className="player-name-label">{row.player_name}</div> : null}
                      </div>
                    </td>
                    <td>{row.item_name}</td>
                    <td>{row.qty}</td>
                    <td><span className={`refund-queue-status refund-queue-status-${row.status}`}>{t(`refundQueue.${row.status}`) || row.status}</span></td>
                    <td>{row.attempts}</td>
                    <td>{row.delivery_method || '-'}</td>
                    <td className="refund-queue-error-cell" title={row.last_error || ''}>{row.last_error || '-'}</td>
                    <td>{new Date(row.created_at).toLocaleString()}</td>
                    <td className="refund-queue-row-actions">
                      <button className="btn btn-secondary" onClick={() => retryRow(row.id)} disabled={loading || !hasPermission('refund.edit')}>{t('refundQueue.retry')}</button>
                      <button className="btn btn-danger" onClick={() => deleteRow(row.id)} disabled={loading || !hasPermission('refund.edit')}>{t('refundQueue.delete')}</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="refund-queue-pagination">
          <div>{t('refundQueue.totalItems', { count: stats.total })}</div>
          <div className="refund-queue-pages">
            <button className="btn btn-secondary" onClick={() => setPage(Math.max(1, page-1))} disabled={page===1}>‹</button>
            <span className="refund-queue-page-indicator">{page}</span>
            <button className="btn btn-secondary" onClick={() => setPage(page+1)} disabled={(page*pageSize) >= stats.total}>›</button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
